/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent } from 'react';

import './HighlightTexts.scss';

interface HighlightTextsProps {
    text: string;
}

/**
 * Component to highlight portions of text with specified class names based on pattern matching.
 *
 * Rules:
 * The input string can contain one or more patterns in the format `$class-name: words$`.
 *    - `$` acts as a marker to mark the start and end of each pattern.
 *    - `class-name` specifies the CSS class to be applied to the `words`.
 *
 * Text outside of `$...$` patterns remains unchanged and is rendered as-is.
 *
 * For each matched segment:
 *    - Create a `<span>` element with `className` set to the captured `class-name`.
 *    - The `words` are rendered within this `<span>`.
 *    - Push non-matching text segments (if any) directly as plain text.
 */
const HighlightTexts: FunctionComponent<HighlightTextsProps> = ({ text }): JSX.Element => {
    // Regular expression to match `$class-name: words$` like patterns.
    const regex = /\$(\S+):(.*?)\$/g;
    const textFragment: React.ReactNode[] = [];
    let lastIndex = 0;
    let match = regex.exec(text);

    if (!text) {
        return <></>;
    }

    while (match) {
        // Pushes plain text in array.
        if (match.index > lastIndex) {
            textFragment.push(text.substring(lastIndex, match.index));
        }
        const className = match[1].trim();
        const content = match[2].trim();

        textFragment.push(<span className={className}>{content}</span>);

        lastIndex = regex.lastIndex;
        match = regex.exec(text);
    }

    if (lastIndex < text.length && text.length > 0) {
        textFragment.push(text.substring(lastIndex));
    }

    return <>{textFragment}</>;
};

export default HighlightTexts;
